import { useEffect, useState } from "react";
import type { LoginViewState, ViewExtra } from "../types";
import { buildViewUrl, getParams, resolveInitialView } from "../utils/loginRouting";

const VIEW_TITLES: Record<LoginViewState, string> = {
  login: "Sign in - Klipboard CRM",
  forgotPassword: "Forgotten Password - Klipboard CRM",
  forgotPasswordSent: "Password Reset Sent - Klipboard CRM",
  verifyCode: "Verify Code - Klipboard CRM",
  updatePassword: "Update Password - Klipboard CRM",
  resetPassword: "Reset Password - Klipboard CRM",
  confirmEmail: "Confirm Email - Klipboard CRM",
  accountSelection: "Sign in with Microsoft - Klipboard CRM",
};

/**
 * Manages the active view state for the login page.
 * Resolves the initial view from the URL, handles view transitions,
 * keeps the document title in sync, and pushes a history entry per
 * client-side transition so refresh/back/forward restore the correct view.
 */
export const useLoginViewState = () => {
  const [{ view: initialView, extra: initialExtra }] = useState(() => resolveInitialView());

  const [view, setView] = useState<LoginViewState>(initialView);
  const [viewExtra, setViewExtra] = useState<ViewExtra>(initialExtra);

  // SSO error passed back via query string from LoginWithCode MVC fallback
  const [ssoError] = useState<string | null>(() => getParams().get("error"));

  const handleViewChange = (nextView: LoginViewState, extra?: Record<string, string>) => {
    const nextExtra = extra ?? {};
    setViewExtra(nextExtra);
    setView(nextView);
    window.history.pushState({ view: nextView, extra: nextExtra }, "", buildViewUrl(nextView, nextExtra));
  };

  // Attach state to the initial history entry so the first back-navigation has
  // something to restore, and sync view state with the browser's back/forward buttons.
  useEffect(() => {
    window.history.replaceState({ view: initialView, extra: initialExtra }, "");

    const onPopState = (event: PopStateEvent) => {
      const state = event.state as { view?: LoginViewState; extra?: ViewExtra } | null;
      if (state?.view) {
        setView(state.view);
        setViewExtra(state.extra ?? {});
        return;
      }
      const resolved = resolveInitialView();
      setView(resolved.view);
      setViewExtra(resolved.extra);
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [initialView, initialExtra]);

  useEffect(() => {
    document.title = VIEW_TITLES[view];
  }, [view]);

  return { view, viewExtra, handleViewChange, ssoError };
};
